import { useState } from 'react';

import { Box, Stack, Typography } from '@mui/material';
import { Carousel } from '@realadvisor/carousel';
import { Image } from '@realadvisor/image';

import { NoPhotosPlaceholder } from '@modules/_shared/components/NoPhotoPlaceholder';

export type PropertyCarouselImage =
  | { kind: 'url'; id: string; url: string }
  | { kind: 'bucket'; id: string; bucketName: string; fileName: string };

type PropertyImagesCarouselProps = {
  images: PropertyCarouselImage[];
  topLeftBadge?: string | null;
  topRightBadge?: string | null;
};

const IMAGE_RATIO = 2 / 3;
const ITEMS_ON_SCREEN = 3.5;

export const PropertyImagesCarousel = ({
  images,
  topLeftBadge,
  topRightBadge,
}: PropertyImagesCarouselProps) => {
  const [bucketLoadError, setBucketLoadError] = useState(false);

  const slots: (PropertyCarouselImage | { kind: 'placeholder'; id: string })[] =
    [...images];
  if (slots.length < ITEMS_ON_SCREEN) {
    const missing = Math.ceil(ITEMS_ON_SCREEN) - slots.length;
    for (let i = 0; i < missing; i++) {
      slots.push({ kind: 'placeholder', id: `placeholder-${i}` });
    }
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Carousel
        media={[
          { gap: 5, itemsOnScreen: 1.5, aspectRatio: IMAGE_RATIO },
          { gap: 5, itemsOnScreen: 2.5, aspectRatio: IMAGE_RATIO },
          { gap: 5, itemsOnScreen: ITEMS_ON_SCREEN, aspectRatio: IMAGE_RATIO },
        ]}
      >
        {slots.map(item => {
          if (item.kind === 'url') {
            return (
              <Image
                key={item.id}
                css={{ width: '100%', height: '100%', objectFit: 'cover' }}
                loading="lazy"
                options={{ w: 400, f: 'jpg' }}
                objectFit="cover"
                src={item.url}
              />
            );
          }
          if (item.kind === 'bucket' && !bucketLoadError) {
            return (
              <Image
                key={item.id}
                css={{ width: '100%', height: '100%', objectFit: 'cover' }}
                src={{ bucketName: item.bucketName, fileName: item.fileName }}
                onError={() => setBucketLoadError(true)}
              />
            );
          }
          return <NoPhotosPlaceholder key={item.id} ratio={IMAGE_RATIO} />;
        })}
      </Carousel>

      <Stack
        spacing={1}
        sx={{ position: 'absolute', zIndex: 1, top: 16, left: 16 }}
        direction="row"
      >
        {topLeftBadge != null && topLeftBadge !== '' && (
          <Box
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: 1,
              bgcolor: 'rgb(52, 52, 52)',
              color: '#fff',
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {topLeftBadge}
            </Typography>
          </Box>
        )}
        {topRightBadge != null && topRightBadge !== '' && (
          <Box sx={{ px: 1, py: 0.5, borderRadius: 1, bgcolor: 'grey.300' }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {topRightBadge}
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
};
